import {
  type ResourceType,
  type Provider,
  type Condition,
  ConditionOperatorEnum,
} from '@repo/api-gw-sdk';
import type { ColumnDefBase } from '@tanstack/react-table';
import type React from 'react';

export function isCondition(value: unknown): value is Condition {
  return (
    typeof value === 'object' &&
    value !== null &&
    'operator' in value &&
    typeof value.operator === 'string'
  );
}

export function isValueCondition(condition: Condition | undefined): boolean {
  return !!condition && !isCombineCondition(condition);
}

export function isCombineCondition(condition: Condition | undefined): boolean {
  return (
    condition?.operator === ConditionOperatorEnum.And ||
    condition?.operator === ConditionOperatorEnum.Or
  );
}

export const createCombineCondition = (
  conditions: Condition[] = [],
  operator = ConditionOperatorEnum.And
): Condition => ({
  operator,
  conditions,
});

export const createValueCondition = (
  property: string,
  value: string[]
): Condition => ({
  property,
  operator: ConditionOperatorEnum.ContainsAnyOf,
  value,
});

export function createFromCondition(
  property: string,
  value: string
): Condition {
  return {
    operator: ConditionOperatorEnum.From,
    property,
    value: [value],
  };
}

export function createToCondition(property: string, value: string): Condition {
  return {
    operator: ConditionOperatorEnum.To,
    property,
    value: [value],
  };
}

export const createAllIncludedCondition = (): Condition => ({
  property: '',
  operator: '',
  conditions: [],
  value: ['true'],
});

export const createAllExcludedCondition = (): Condition => ({
  property: '',
  operator: '',
  conditions: [],
  value: ['false'],
});

export function removeValueCondition(
  filters: Condition,
  condition: Condition,
  value: string
): Condition {
  const updatedCondition = condition.value?.filter((x) => x !== value) || [];

  const newCondition = updatedCondition.length
    ? { ...condition, value: updatedCondition }
    : undefined;

  return replaceValueCondition(filters, condition, newCondition);
}

function replaceValueCondition(
  filters: Condition,
  oldCondition: Condition,
  newCondition: Condition | undefined
) {
  if (isValueCondition(filters)) {
    return filters;
  }

  return {
    ...filters,
    conditions: filters.conditions?.reduce<Condition[]>((agg, val) => {
      if (val === oldCondition) {
        if (newCondition) {
          agg.push(newCondition);
        }
      } else {
        agg.push(replaceValueCondition(val, oldCondition, newCondition));
      }

      return agg;
    }, []),
  };
}

export function collectValueConditions(condition: Condition): Condition[] {
  return isValueCondition(condition)
    ? [condition]
    : condition.conditions?.flatMap(collectValueConditions) || [];
}

export function findValueCondition(
  conditions: Condition[],
  propertyName: string | undefined
): Condition | undefined {
  return conditions.find(
    (x) => isValueCondition(x) && x.property === propertyName
  );
}

export function conditionValues(condition: Condition | undefined): string[] {
  return isValueCondition(condition) ? (condition?.value ?? []) : [];
}

export enum PropertyType {
  Date = 'Date',
  String = 'String',
  SingleValue = 'SingleValue',
  MultipleValues = 'MultipleValues',
}

export enum PropertyPolicyRelation {
  SingleValue = 'SingleValue',
  MultipleValues = 'MultipleValues',
  String = 'String',
  Boolean = 'Boolean',
}

interface FieldComponentProps<T> {
  entity: T;
  onChange?: () => Promise<void>;
}

/**
 * Set by a host that renders a condition inside a popover it owns (the filter
 * strip anchors one to each chip). Its presence — not a set of independent
 * boolean props — is what puts a condition component in hosted mode: no trigger
 * button of its own, open from mount, anchored to the host, and no free-text
 * "add new" affordance. Absent when the condition renders inline, as in the
 * condition builder.
 */
export interface ConditionPanelHost {
  /**
   * The mounted element to anchor to, not a ref to it: MUI's Popover wants an
   * element, and a ref would have to be read during render — where it is still
   * null on the commit the popover mounts in, and mutating it later does not
   * re-render.
   */
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/** One selectable value, shared by every filter and condition selection surface. */
export interface FilterOption {
  id: string;
  name?: string;
  Image?: React.ReactNode;
  description?: React.ReactNode;
  isGroupTitle?: boolean;
  /** Set when the Image already carries the label, so the name isn't printed twice. */
  hideName?: boolean;
}

export interface MultiSelectConditionPanelProps {
  property: FilterProperty;
  entityName: string;
  isLoading: boolean;
  options: FilterOption[];
  condition: Condition;
  onChange: (condition: Condition) => void;
  showAddNewButton: boolean;
  showSearchBox: boolean;
  useImagesInSelectedView?: boolean;
  showNameFirst?: boolean;
  shouldShowId?: boolean;
  showSelectAll?: boolean;
  panelHost?: ConditionPanelHost;
  /** Runs whenever the popover closes, hosted or not — for the wrapper's own cleanup. */
  onPanelClose?: () => void;
  onSearchChange?: (value: string) => void;
}

export interface FilterProps {
  property: FilterProperty;
  conditions: Condition[];
  'data-testid'?: string;
  onChange: (conditions: Condition[]) => void;
}

/**
 * Drill-down is a capability of the accordion filters panel alone — it swaps its
 * own body for the component passed here. Only properties rendered by that panel
 * can ask for it, so it stays off the base props every other surface receives.
 */
export interface DrillDownFilterProps extends FilterProps {
  renderDrillDownFilter?: (
    title: string,
    subtitle: string,
    component: (props: { filter: Condition }) => React.ReactElement
  ) => void;
}

/**
 * Which value set a condition offers. The filter strip narrows a property to the
 * values present in the current inventory ('discovered'); every other surface —
 * the condition editor for policies and notifications — offers all possible
 * values ('all'), since those can target values not yet discovered.
 */
type ConditionOptionScope = 'discovered' | 'all';

export interface MultiSelectConditionComponentProps {
  condition: Condition;
  onChange: (condition: Condition) => void;
  property: FilterProperty;
  supportedProviders?: Provider[];
  supportedResourceTypes?: ResourceType[];
  panelHost?: ConditionPanelHost;
  /** Defaults to 'all' when unset. See {@link ConditionOptionScope}. */
  optionScope?: ConditionOptionScope;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FilterProperty<T = any> {
  name: string;
  displayName: string;
  type: PropertyType;
  /**
   * Optional override for how many values are shown in the filter chip count.
   * Use when stored condition values differ from what the user selects in the UI.
   */
  getConditionValueCount?: (value: string[]) => number;
  policyRelation?: PropertyPolicyRelation;
  propertyPredicate?: (providers: Provider[]) => boolean;
  group: string;
  linkedProperties?: FilterProperty[];
  column?: ColumnDefBase<T>;
  accessorFn?: (row: T) => unknown;
  field?: {
    getTitle?: (entity: T) => string;
    Component: (props: FieldComponentProps<T>) => React.ReactNode;
  };
  filter?: {
    fullPanelComponent?: {
      title: string;
      subtitle?: string;
      /**
       * Optional hook. When provided, the full-panel drill-down is only used
       * if the hook returns `true`; otherwise the property falls back to
       * rendering its inline `Component` inside the accordion. Defaults to
       * always-on (full-panel) behavior when omitted.
       */
      useShouldUseFullPanel?: () => boolean;
      Component: (
        props: DrillDownFilterProps & {
          onDoneClick: () => void;
        }
      ) => React.ReactNode;
    };
    expandedByDefault?: boolean;
    Component?: (props: DrillDownFilterProps) => React.ReactNode;
    Badge?: ({ conditionValue }: { conditionValue: string }) => React.ReactNode;
    shouldHideBadge?: (condition: Condition) => boolean;
  };
  condition?: {
    /**
     * Label used in the condition builder (PropertySelect / ConditionViewer).
     * Falls back to the property `displayName` when omitted. Use this when the
     * condition builder needs a more explicit label than the table column /
     * filter chip — e.g. tagKeyValues shows "Tags" as a column but
     * "Tag key-value pairs" here.
     */
    displayName?: string;
    /**
     * Groups this property under a fly-out sub-menu in the condition property
     * picker (PropertySelect). Properties sharing the same `group` collapse
     * into a single parent entry (e.g. "Resource tags" → "Tag keys" /
     * "Tag key-value pairs"). `label` is the short text shown inside the
     * sub-menu; the full condition label still comes from
     * `condition.displayName` / `displayName`.
     */
    subMenu?: {
      group: string;
      label: string;
    };
    Component: (props: MultiSelectConditionComponentProps) => React.ReactNode;
  };
}

export const isAnAllCondition = (condition: Condition) => {
  return isEmptyCondition(condition) && condition.value?.length === 1;
};

export const isEmptyCondition = (condition: Condition) => {
  return (
    !condition.property && !condition.operator && !condition.conditions?.length
  );
};

export const hasValidConditionValues = (condition?: Condition): boolean => {
  if (!condition) {
    return false;
  }

  if (condition.value !== undefined) {
    return !!condition.value.length;
  }

  if (condition.conditions?.length) {
    return condition.conditions.every(hasValidConditionValues);
  }

  return false;
};
